<script setup>
import Icon from '@/Components/Icon/index.vue';
import Button from '@/Components/Button.vue';
import { computed } from 'vue';
import ListItem from '@/Components/List/Item/index.vue';
import { usePage } from '@inertiajs/vue3';
import { THEMES } from '@/constants';

const props = defineProps({
  scholarship: {
    type: Object,
    required: true,
  }
});

const auDollarFormatter = (dollars) => `AUD $${Intl.NumberFormat().format(dollars)}`;

const levelsOfStudy = computed(() => {
  if(!props.scholarship.level_of_studies.length) {
    return null;
  }
  return props.scholarship.level_of_studies.map((los) => {
    return los.name;
  })
  .join(', ');
});
const providerLogos = computed(() => {
  if(!props.scholarship.organisations.length) {
    return null;
  }

  return props.scholarship.organisations[0].logos;
});

const altText = computed(() => {
  if(!props.scholarship.organisations.length) {
    return props.scholarship.name;
  }
  return props.scholarship.organisations[0].name;
});

const page = usePage();
const context = computed(() => page.props?.context);
const theme = context.value.theme;

const showEnquiry = (context.value.enquiry_enabled ?? false) && (context.value.scholarship_enquiry_enabled ?? true);

const learnMoreBtnVariant = (theme) => {
  return {
    [THEMES.STUDY_AUS]: 'link',
    [THEMES.STUDY_MELB]: 'secondary',
    [THEMES.STUDY_QLD]: 'secondary',
  }[theme];
};
</script>

<template>
  <ListItem
  class="scholarship-list-card" 
  fallback-icon="GraduationHat"
:alt-text="altText"
          :image-set="providerLogos" :logo-link="`/provider/${scholarship.organisations[0].slug}/${scholarship.organisations[0].id}`">
  <template #title>
    <a
            :href="`/scholarship/${scholarship.slug}/${scholarship.id}`"
            class="border-none"
          >{{ scholarship.name }}</a>
  </template>
  <template #subtitle>
    <strong><a
            v-if="scholarship.organisations.length"
            :href="`/provider/${scholarship.organisations[0].slug}/${scholarship.organisations[0].id}`"
            class="border-none inline-block my-1"
            :aria-label="`Scholarship provider ${scholarship.organisations[0].name}`"
          >{{ scholarship.organisations[0].name }}</a></strong>
  </template>
  <template #content>
    <p
          v-if="scholarship.amount_annual"
          class="text-sm mb-2 flex align-items-center"
        >
          <Icon
            class="me-2 self-center"
            path="DollarSquare"

            :height="16"
            :width="16"
          />Award value - {{ auDollarFormatter(scholarship.amount_annual) }} annually
        </p>
        <p
          v-if="levelsOfStudy"
          class="text-sm mb-2 flex align-items-center"
        >
          <Icon
            class="me-2 self-center"
            path="Book"
            :height="16"
            :width="16"
          />Level of study - {{ levelsOfStudy }}
        </p>
        <p
          v-if="scholarship.country_of_citizenship.name"
          class="text-sm mb-2 flex align-items-center"
        >
          <Icon
            class="me-2 self-center"
            path="Flag"
            :height="16"
            :width="16"
          />Nationality - {{ scholarship.country_of_citizenship.name }}
        </p>
        <p
          v-if="scholarship.closing_date"
          class="text-sm mb-2 flex align-items-center"
        >
          <Icon
            class="me-2 self-center"
            path="Calendar"
            :height="16"
            :width="16"
          />Closing date - {{ scholarship.closing_date }}
        </p>
  </template>

  <template v-if="showEnquiry" #action1>
    <Button
      type="link"
      :aria-label="`Enquire about ${scholarship.name}`"
      :link="`/scholarship/${scholarship.slug}/${scholarship.id}/enquiry`"
      class="w-full md:w-fit"
    >
      Enquire
    </Button>
  </template>
  <template #action2>
    <Button
      :variant="learnMoreBtnVariant(theme)"
      type="link"
      :aria-label="`Learn more about ${scholarship.name}`"
      :link="`/scholarship/${scholarship.slug}/${scholarship.id}`"
      class="w-full md:w-fit "
    >
      Learn More
    </Button>
  </template>
  </ListItem>
</template>
